import * as THREE from "three";

export type SkyConfig = {
  zenithColor?: number;
  horizonColor?: number;
  nadirColor?: number;
  sunColor?: number;
  sunDirection?: THREE.Vector3;
  horizonSoftness?: number;
  hazeStrength?: number;
  sunGlow?: number;
  sunSharpness?: number;
};

const DEFAULT_CONFIG: Required<Omit<SkyConfig, "sunDirection">> = {
  zenithColor: 0x365d8d,
  horizonColor: 0xa7b68d,
  nadirColor: 0x566353,
  sunColor: 0xfff1c0,
  horizonSoftness: 0.58,
  hazeStrength: 0.22,
  sunGlow: 0.26,
  sunSharpness: 22,
};

export class Sky {
  readonly mesh: THREE.Mesh;
  readonly environmentScene: THREE.Scene;
  private readonly probeMesh: THREE.Mesh;

  constructor(config: SkyConfig = {}) {
    const merged = { ...DEFAULT_CONFIG, ...config };
    const sunDirection = (config.sunDirection ?? new THREE.Vector3(0.8, 0.9, 0.35)).clone().normalize();
    // BackSide alone renders the sphere's inside. (Do NOT also scale(-1,1,1):
    // the two inversions cancel and the sky becomes invisible from within —
    // that exact bug shipped for months and made the sky a black void.)
    const geo = new THREE.SphereGeometry(1400, 32, 16);

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uZenith: { value: new THREE.Color(merged.zenithColor) },
        uHorizon: { value: new THREE.Color(merged.horizonColor) },
        uNadir: { value: new THREE.Color(merged.nadirColor) },
        uSunColor: { value: new THREE.Color(merged.sunColor) },
        uSunDirection: { value: sunDirection },
        uHorizonSoftness: { value: merged.horizonSoftness },
        uHazeStrength: { value: merged.hazeStrength },
        uSunGlow: { value: merged.sunGlow },
        uSunSharpness: { value: merged.sunSharpness },
      },
      vertexShader: /* glsl */`
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        uniform vec3 uZenith;
        uniform vec3 uHorizon;
        uniform vec3 uNadir;
        uniform vec3 uSunColor;
        uniform vec3 uSunDirection;
        uniform float uHorizonSoftness;
        uniform float uHazeStrength;
        uniform float uSunGlow;
        uniform float uSunSharpness;
        varying vec3 vDir;
        void main() {
          float up = clamp(vDir.y * 0.5 + 0.5, 0.0, 1.0);
          float horizonBand = 1.0 - abs(vDir.y);
          horizonBand = pow(horizonBand, mix(1.9, 0.8, uHorizonSoftness));

          vec3 sky = mix(uNadir, uHorizon, smoothstep(0.0, 0.48, up));
          sky = mix(sky, uZenith, smoothstep(0.42, 1.0, up));
          sky += uHorizon * horizonBand * uHazeStrength;

          float sunAmount = max(dot(normalize(vDir), normalize(uSunDirection)), 0.0);
          float sunDisk = pow(sunAmount, uSunSharpness);
          float sunHalo = pow(sunAmount, 3.5);
          sky += uSunColor * (sunHalo * uSunGlow + sunDisk * 0.22);

          gl_FragColor = vec4(sky, 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false,
    });

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.renderOrder = -1;
    this.mesh.userData["isVFX"] = true;

    this.probeMesh = new THREE.Mesh(geo, mat);
    this.probeMesh.renderOrder = -1;
    this.probeMesh.userData["isVFX"] = true;

    this.environmentScene = new THREE.Scene();
    this.environmentScene.add(this.probeMesh);
  }
}
